import AgentExamplesImport from "@/components/AgentExamplesImport";
import AgentCollaboration from "@/components/AgentCollaboration";
import MobilityPreferences from "@/components/MobilityPreferences";
import HelpLink from "@/components/HelpLink";
import AgentDocumentThemes from "@/components/AgentDocumentThemes";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Save, AlertTriangle, XCircle, Plus, Trash2 } from "lucide-react";
import { api, useApi, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Card, Field, Sel, LinesInput, Can } from "@/components/common";
import ModelPicker from "@/components/ModelPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const VOICES = ["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"].map((v) => ({ value: v, label: v }));

function Examples({ value, onChange }) {
  const list = value || [];
  const upd = (i, k, v) => onChange(list.map((e, j) => (j === i ? { ...e, [k]: v } : e)));
  return (
    <div className="space-y-2">
      {list.map((e, i) => (
        <div key={i} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
          <Input placeholder="Question" value={e.q} onChange={(x) => upd(i, "q", x.target.value)} data-testid={`example-q-${i}`} />
          <Input placeholder="Réponse attendue" value={e.a} onChange={(x) => upd(i, "a", x.target.value)} data-testid={`example-a-${i}`} />
          <Button size="icon" variant="ghost" onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => onChange([...list, { q: "", a: "" }])} data-testid="add-example-btn"><Plus className="h-3.5 w-3.5 mr-1" />Exemple</Button>
    </div>
  );
}

function ToolsTab({ cfg, set, connections }) {
  const tools = cfg.tools || [];
  const get = (cid) => tools.find((t) => t.connection_id === cid);
  const update = (cid, patch) => set({ tools: get(cid) ? tools.map((t) => (t.connection_id === cid ? { ...t, ...patch } : t)) : [...tools, { connection_id: cid, endpoint_ids: [], allow_write: false, ...patch }] });
  return (
    <div className="space-y-3">
      <div className="inline-help-note"><HelpLink topic="outils-confirmation" label="Autoriser les outils de l’agent" compact={false} /></div>
      {(connections || []).some(c => get(c.id) && (c.endpoints || []).some(ep => ep.path === "/journeyplanner/api/opt/PlanTrips/json" && (!get(c.id).endpoint_ids?.length || get(c.id).endpoint_ids.includes(ep.id)))) && <MobilityPreferences value={cfg.mobility} onChange={mobility=>set({mobility})} />}
      {(connections || []).map((c) => {
        const t = get(c.id);
        return (
          <Card key={c.id} className="p-4">
            <div className="flex items-center gap-3">
              <Checkbox checked={!!t} data-testid={`tool-conn-${c.id}`} onCheckedChange={(v) => (v ? update(c.id, {}) : set({ tools: tools.filter((x) => x.connection_id !== c.id) }))} />
              <div className="flex-1"><div className="font-medium text-sm">{c.name}</div><div className="text-xs text-muted-foreground">{c.kind === "web" ? `Source web · ${c.web?.mode}` : `${c.endpoints.length} endpoint(s)`}</div></div>
              {t && c.kind === "api" && <label className="flex items-center gap-2 text-xs"><Switch checked={!!t.allow_write} onCheckedChange={(v) => update(c.id, { allow_write: v })} data-testid={`tool-write-${c.id}`} />Autoriser les modifications</label>}
            </div>
            {t && c.kind === "api" && (
              <div className="mt-3 pl-7 grid sm:grid-cols-2 gap-1.5">
                {c.endpoints.map((ep) => (
                  <label key={ep.id} className="flex items-center gap-2 text-xs">
                    <Checkbox checked={!t.endpoint_ids?.length || t.endpoint_ids.includes(ep.id)}
                      onCheckedChange={(v) => {
                        const all = c.endpoints.map((e) => e.id);
                        const cur = t.endpoint_ids?.length ? t.endpoint_ids : all;
                        update(c.id, { endpoint_ids: v ? [...cur, ep.id] : cur.filter((x) => x !== ep.id) });
                      }} />
                    <span className="mono">{ep.method}</span>{ep.name}<StatusBadge status={ep.access} />{ep.require_confirmation && <span className="text-amber-500">confirmation</span>}
                  </label>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function RoutingTab({ cfg, set, agents, catalog }) {
  const r = cfg.routing || {};
  const setR = (patch) => set({ routing: { ...r, ...patch } });
  const others = (agents || []).filter((a) => a.kind !== "accueil");
  const opts = others.map((a) => ({ value: a.id, label: a.name }));
  return (
    <div className="space-y-6">
      <Field label="Méthode de routage" helpTopic="routage"><Sel testid="routing-strategy" value={r.strategy || "ai"} onChange={strategy=>setR({strategy})} options={[{value:"rules_only",label:"Règles uniquement — sans modèle"},{value:"ai",label:"Règles + classification IA"}]} /></Field>
      {r.strategy === "rules_only" && <p className="text-sm text-muted-foreground">Une règle unique choisit l’agent. Plusieurs règles demandent le choix de l’usager. Sans correspondance, l’agent de secours est utilisé. Aucun modèle n’est appelé pour décider du routage ni résumer le transfert ; les agents continuent d’utiliser leurs modèles pour dialoguer.</p>}
      <Field label="Agents cibles possibles">
        <div className="grid sm:grid-cols-2 gap-2">
          {others.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-sm">
              <Checkbox checked={(r.target_agent_ids || []).includes(a.id)} data-testid={`route-target-${a.id}`}
                onCheckedChange={(v) => setR({ target_agent_ids: v ? [...(r.target_agent_ids || []), a.id] : r.target_agent_ids.filter((x) => x !== a.id) })} />{a.name}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Agent de secours (aucune compétence ne correspond)"><Sel testid="route-fallback-select" value={r.fallback_agent_id} onChange={(v) => setR({ fallback_agent_id: v })} options={opts} /></Field>
      <Field label="Règles configurables (prioritaires sur la classification IA si une seule règle correspond)">
        <div className="space-y-2">
          {(r.rules || []).map((rule, i) => (
            <div key={i} className="grid sm:grid-cols-[140px_1fr_200px_auto] gap-2">
              <Input value={rule.label} placeholder="Libellé" onChange={(e) => setR({ rules: r.rules.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
              <Input value={(rule.keywords || []).join(", ")} placeholder="mots-clés séparés par des virgules" data-testid={`rule-keywords-${i}`}
                onChange={(e) => setR({ rules: r.rules.map((x, j) => (j === i ? { ...x, keywords: e.target.value.split(",").map((k) => k.trim()) } : x)) })} />
              <Sel value={rule.target_agent_id} onChange={(v) => setR({ rules: r.rules.map((x, j) => (j === i ? { ...x, target_agent_id: v } : x)) })} options={opts} />
              <Button size="icon" variant="ghost" onClick={() => setR({ rules: r.rules.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setR({ rules: [...(r.rules || []), { label: "", keywords: [], target_agent_id: "" }] })} data-testid="add-rule-btn"><Plus className="h-3.5 w-3.5 mr-1" />Règle</Button>
        </div>
      </Field>
      <Field label="Nombre max. de questions de clarification"><Input type="number" className="w-32" value={r.max_clarifications ?? 2} onChange={(e) => setR({ max_clarifications: parseInt(e.target.value) })} /></Field>
      {r.strategy !== "rules_only" && <Card><h4 className="font-semibold text-sm mb-3">Modèle de classification (routage)</h4><ModelPicker testid="classifier-llm" catalog={catalog} value={r.classifier_llm} onChange={(v) => setR({ classifier_llm: v })} task="routage" /></Card>}
    </div>
  );
}

export default function AgentEditor() {
  const { id } = useParams();
  const { can } = useAuth();
  const { data: agent, reload } = useApi(`/agents/${id}`);
  const { data: versions, reload: reloadV } = useApi(`/agents/${id}/versions`);
  const { data: check, reload: reloadC } = useApi(`/agents/${id}/check`);
  const { data: catalog } = useApi("/providers/catalog");
  const { data: connections } = useApi("/connections");
  const { data: skills } = useApi("/skills");
  const { data: docs, reload: reloadDocs } = useApi("/documents");
  const { data: agents } = useApi("/agents");
  const [a, setA] = useState(null);
  const [note, setNote] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingVersion, setDeletingVersion] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  useEffect(() => { if (agent) setA(agent); }, [agent]);
  if (!a) return <div className="text-muted-foreground">Chargement…</div>;
  const cfg = a.draft;
  const set = (patch) => setA({ ...a, draft: { ...cfg, ...patch } });
  const save = async () => {
    try {
      await api.put(`/agents/${id}`, { name: a.name, kind: a.kind, draft: a.draft });
      toast.success("Brouillon enregistré");
      reload(); reloadC();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const version = async (status) => {
    try {
      await api.put(`/agents/${id}`, { name: a.name, draft: a.draft });
      await api.post(`/agents/${id}/versions`, { status, note });
      toast.success(status === "publiée" ? "Version publiée" : "Version de test créée");
      setNote(""); reload(); reloadV();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const vAct = async (vid, action) => {
    try { await api.post(`/agents/${id}/versions/${vid}/${action}`); toast.success("Effectué"); reload(); reloadV(); } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteVersion = async () => {
    if (!deleteTarget || deletingVersion) return;
    setDeletingVersion(true); setDeleteError("");
    try {
      await api.delete(`/agents/${id}/versions/${deleteTarget.id}`);
      setDeleteTarget(null); reloadV(); toast.success("Version supprimée définitivement");
    } catch (e) { setDeleteError(errMsg(e)); }
    finally { setDeletingVersion(false); }
  };
  const ro = !can("agents:write");
  return (
    <div>
      <Link to="/agents" className="text-sm text-muted-foreground flex items-center gap-1 mb-4 hover:text-foreground"><ArrowLeft className="h-4 w-4" />Agents</Link>
      <PageHeader title={a.name} subtitle={`Brouillon basé sur la v${a.current_version} · les modifications ne sont visibles en production qu'après publication.`}>
        <StatusBadge status={a.kind} /><StatusBadge status={a.status} />
        {!ro && <Button onClick={save} data-testid="save-agent-btn"><Save className="h-4 w-4 mr-1" />Enregistrer le brouillon</Button>}
      </PageHeader>
      {check?.issues?.length > 0 && (
        <Card className="mb-6 border-amber-500/40" data-testid="agent-issues">
          <h4 className="font-semibold text-sm mb-2">Contrôles de configuration</h4>
          {check.issues.map((i, k) => (
            <div key={k} className="flex items-center gap-2 text-sm">{i.level === "erreur" ? <XCircle className="h-4 w-4 text-rose-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />}{i.message}</div>
          ))}
        </Card>
      )}
      <Tabs defaultValue="general">
        <TabsList className="flex-wrap h-auto">
          {[["general", "Général"], ["behavior", "Comportement"], ["examples", "Exemples & infos"], ["models", "Modèles"], ["tools", "Outils"], ["collaboration", "Collaboration"], ["knowledge", "Documents & skills"], ["voice", "Voix"],
            ...(a.kind === "accueil" ? [["routing", "Routage"]] : []), ["versions", "Versions"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`agent-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        <fieldset disabled={ro} className="mt-6">
          <TabsContent value="general" className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nom"><Input data-testid="agent-name-input" value={a.name} onChange={(e) => setA({ ...a, name: e.target.value })} /></Field>
              <Field label="Domaine de compétence"><Input data-testid="agent-domain-input" value={cfg.domain} onChange={(e) => set({ domain: e.target.value })} /></Field>
            </div>
            <Field label="Description (utilisée par le routage)"><Textarea data-testid="agent-description-input" value={cfg.description} onChange={(e) => set({ description: e.target.value })} /></Field>
            <Field label="Prompt système"><Textarea rows={6} data-testid="agent-prompt-input" value={cfg.system_prompt} onChange={(e) => set({ system_prompt: e.target.value })} /></Field>
            <Field label="Consignes complémentaires"><Textarea rows={3} value={cfg.instructions} onChange={(e) => set({ instructions: e.target.value })} /></Field>
            <Field label="Mode"><Sel testid="agent-mode-select" value={cfg.mode} onChange={(v) => set({ mode: v })} options={[{ value: "text", label: "Texte" }, { value: "voice", label: "Voix" }, { value: "both", label: "Texte et voix" }]} /></Field>
          </TabsContent>
          <TabsContent value="collaboration"><AgentCollaboration agentId={a.id} value={cfg.collaboration} agents={agents} onChange={collaboration=>set({collaboration})} /></TabsContent>
          <TabsContent value="behavior" className="space-y-4">
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Langue"><Sel value={cfg.language} onChange={(v) => set({ language: v })} options={[{ value: "fr", label: "Français" }, { value: "en", label: "Anglais" }, { value: "es", label: "Espagnol" }, { value: "de", label: "Allemand" }]} /></Field>
              <Field label="Ton"><Input value={cfg.tone} onChange={(e) => set({ tone: e.target.value })} /></Field>
              <Field label="Niveau de détail"><Sel value={cfg.detail_level} onChange={(v) => set({ detail_level: v })} options={[{ value: "concis", label: "Concis" }, { value: "standard", label: "Standard" }, { value: "détaillé", label: "Détaillé" }]} /></Field>
            </div>
            <Field label="Règles de comportement"><LinesInput value={cfg.behavior_rules} onChange={(v) => set({ behavior_rules: v })} testid="agent-rules-input" /></Field>
            <Field label="Règles de clarification (demande ambiguë)"><Textarea value={cfg.clarification_rules} onChange={(e) => set({ clarification_rules: e.target.value })} /></Field>
            <Field label="Règles en cas d'information manquante ou de panne d'outil"><Textarea value={cfg.fallback_rules} onChange={(e) => set({ fallback_rules: e.target.value })} /></Field>
            <Field label="Message si tous les modèles sont indisponibles"><Textarea value={cfg.fallback_message} onChange={(e) => set({ fallback_message: e.target.value })} /></Field>
          </TabsContent>
          <TabsContent value="examples" className="space-y-6">
            <AgentExamplesImport agentId={id} value={cfg.examples} onChange={(v) => set({ examples: v })} />
            <Field label="Exemples de questions et réponses attendues"><Examples value={cfg.examples} onChange={(v) => set({ examples: v })} /></Field>
            <Field label="Informations à demander avant de répondre ou d'agir"><LinesInput value={cfg.required_info} onChange={(v) => set({ required_info: v })} testid="agent-required-info-input" /></Field>
          </TabsContent>
          <TabsContent value="models" className="space-y-4">
            <Card><h4 className="font-semibold text-sm mb-3">LLM principal</h4><ModelPicker testid="primary-llm" catalog={catalog} value={cfg.llm} onChange={(v) => set({ llm: v })} task="reponse" /></Card>
            <Card><h4 className="font-semibold text-sm mb-3">LLM de secours (en cas d'erreur du principal)</h4><ModelPicker testid="fallback-llm" catalog={catalog} value={cfg.fallback_llm} onChange={(v) => set({ fallback_llm: v })} allowNone /></Card>
          </TabsContent>
          <TabsContent value="tools"><ToolsTab cfg={cfg} set={set} connections={connections} /></TabsContent>
          <TabsContent value="knowledge" className="space-y-6">
            <Field label="Skills affectés (ordre = priorité)">
              <div className="space-y-2">
                {(skills || []).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={cfg.skill_ids.includes(s.id)} data-testid={`agent-skill-${s.id}`} onCheckedChange={(v) => set({ skill_ids: v ? [...cfg.skill_ids, s.id] : cfg.skill_ids.filter((x) => x !== s.id) })} />
                    {s.name}<StatusBadge status={s.status} />
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Documents accessibles par thème">
              <AgentDocumentThemes documents={docs || []} agentId={id} onReload={reloadDocs}
                disabled={ro || !can('documents:write')} />
            </Field>
            <Field label="Recherche documentaire" hint="À la demande : le modèle consulte les documents seulement lorsqu’il utilise l’outil documentaire. Les autorisations restent identiques.">
              <Sel testid="agent-document-search-mode" value={cfg.document_search_mode || 'automatic'} onChange={(v) => set({ document_search_mode: v })}
                options={[{value:'automatic',label:'Automatique à chaque demande'},{value:'on_demand',label:'À la demande de l’agent'}]} />
              <HelpLink topic="documents" label="Quand effectuer une recherche documentaire" compact={false} />
            </Field>
          </TabsContent>
          <TabsContent value="voice" className="grid sm:grid-cols-2 gap-4">
            <Field label="Voix TTS"><Sel testid="agent-voice-select" value={cfg.voice?.voice} onChange={(v) => set({ voice: { ...cfg.voice, voice: v } })} options={VOICES} /></Field>
            <Field label="Modèle TTS"><Sel value={cfg.voice?.tts_model} onChange={(v) => set({ voice: { ...cfg.voice, tts_model: v } })} options={[{ value: "tts-1", label: "tts-1 (rapide)" }, { value: "tts-1-hd", label: "tts-1-hd (qualité)" }]} /></Field>
            <Field label="Vitesse"><Input type="number" step="0.05" min="0.5" max="2" value={cfg.voice?.speed} onChange={(e) => set({ voice: { ...cfg.voice, speed: parseFloat(e.target.value) } })} /></Field>
            <Field label="Langue de reconnaissance"><Input value={cfg.voice?.stt_language} onChange={(e) => set({ voice: { ...cfg.voice, stt_language: e.target.value } })} /></Field>
          </TabsContent>
          <TabsContent value="routing"><RoutingTab cfg={cfg} set={set} agents={agents} catalog={catalog} /></TabsContent>
        </fieldset>
        <TabsContent value="versions" className="space-y-4">
          <HelpLink topic="versions-agents" label="Versions et suppression de l’historique" compact={false} />
          <Can perm="agents:write">
            <Card className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <Field label="Note de version" className="flex-1"><Input data-testid="version-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
              <Button variant="outline" onClick={() => version("test")} data-testid="create-test-version-btn">Créer une version de test</Button>
              <Can perm="agents:publish"><Button onClick={() => version("publiée")} data-testid="publish-version-btn">Publier le brouillon</Button></Can>
            </Card>
          </Can>
          <div className="agent-versions-list" role="region" aria-label="Historique des versions de l’agent" tabIndex={0}>
          {(versions || []).map((v) => (
            <Card key={v.id} className="flex items-center justify-between gap-3 p-4" data-testid={`version-row-${v.version}`}>
              <div><div className="font-semibold">v{v.version} <StatusBadge status={v.status} /></div><div className="text-xs text-muted-foreground">{fmtDate(v.created_at)} · {v.created_by} · {v.note}</div></div>
              <div className="flex flex-wrap gap-2">
                {v.status === "test" && <Can perm="agents:publish"><Button size="sm" onClick={() => vAct(v.id, "promote")} data-testid={`promote-version-${v.version}`}>Publier</Button></Can>}
                <Can perm="agents:write"><Button size="sm" variant="ghost" onClick={() => vAct(v.id, "restore")} data-testid={`restore-version-${v.version}`}>Restaurer dans le brouillon</Button></Can>
                <Can perm="agents:write">{v.id === agent?.published_version_id || v.id === agent?.test_version_id
                  ? <span className="text-xs text-muted-foreground self-center">{v.id === agent?.published_version_id ? "En production — protégée" : "En test — protégée"}</span>
                  : <Button size="sm" variant="destructive" onClick={() => {setDeleteError(""); setDeleteTarget(v);}} data-testid={`delete-version-${v.version}`}>Supprimer</Button>}
                </Can>
              </div>
            </Card>
          ))}
          </div>
        </TabsContent>
      </Tabs>
      <Dialog open={!!deleteTarget} onOpenChange={(open) => {if (!open && !deletingVersion) setDeleteTarget(null);}}>
        <DialogContent><DialogHeader>
          <DialogTitle>Supprimer définitivement la version v{deleteTarget?.version} ?</DialogTitle>
          <DialogDescription>Cette version de l’agent {a.name} ne pourra plus être restaurée ou publiée. Cette action est irréversible.</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">Le brouillon, les autres versions et l’historique des conversations sont conservés. Les versions actuellement publiées ou utilisées en test sont protégées.</p>
        {deleteError && <p role="alert" className="text-sm text-destructive">{deleteError}</p>}
        <DialogFooter><Button variant="outline" disabled={deletingVersion} onClick={() => setDeleteTarget(null)}>Annuler</Button>
          <Button variant="destructive" disabled={deletingVersion} onClick={deleteVersion} data-testid="confirm-delete-version">{deletingVersion ? "Suppression…" : "Supprimer définitivement"}</Button>
        </DialogFooter></DialogContent>
      </Dialog>
    </div>
  );
}

